import { useQuery } from '@tanstack/react-query';
import { agentQueryOptions } from '../../api/agents-fetch.api';
import type { Agent } from '../agent.types';

/**
 * Per-workspace single-agent fetch hook backed by the shared
 * `agentQueryOptions` cache (`['agent', agentId]`).
 *
 * Because entities MUST NOT value-import sibling entities,
 * the caller is responsible for resolving `workspaceId` (e.g. via
 * `useCurrentWorkspaceId` from `@/entities/workspace`) and passing it in.
 *
 * The fetch is gated on both a non-empty `agentId` AND a defined
 * `workspaceId` — matching the legacy gating behaviour where the query was
 * disabled until the user's current workspace had loaded.
 *
 * @param agentId - The ID of the agent to fetch (undefined → no fetch)
 * @param workspaceId - Active workspace context (undefined/null → no fetch)
 * @returns `isMissing` once the fetch settled without a card (gone, not the caller's, or failed),
 * so a consumer can stop waiting on an agent that will never arrive.
 */
export const useFeatureAgent = (
  agentId?: string,
  workspaceId?: string | null,
): { agent: Agent | undefined; isLoading: boolean; isMissing: boolean } => {
  const { data, isLoading, status } = useQuery({
    ...agentQueryOptions(agentId ?? '', workspaceId),
    enabled: !!agentId && !!workspaceId,
  });

  return {
    agent: data ?? undefined,
    isLoading,
    isMissing: status === 'error' || (status === 'success' && data === null),
  };
};
